import type { BigIntish } from '@morpho-org/blue-sdk'

import { toBigInt } from '@morpho-org/dapp-core'
import { isDefined, Time } from '@morpho-org/morpho-ts'

export const defaultTimeFrame: Time.PeriodLike = {
  unit: 'mo',
  duration: 3
}

export const vaultV2DefaultTimeFrame: Time.PeriodLike = 'w'

export interface TimeFrameOption {
  label: string
  value: Time.PeriodLike | null
  disabled?: boolean
}

export const timeFrameOptions: TimeFrameOption[] = [
  {
    label: 'All time',
    value: null
  },
  {
    label: '3 months',
    value: {
      unit: 'mo',
      duration: 3
    }
  },
  {
    label: '1 month',
    value: 'mo'
  },
  {
    label: '1 week',
    value: 'w'
  }
]

/**
 * Returns timeframe options with disabled state based on vault creation timestamp.
 * Options are disabled if the vault is younger than 1 week.
 * @param creationTimestamp - The vault creation timestamp in seconds
 * @param now - The current timestamp in seconds
 */
export function getTimeFrameOptionsForVaultAge(
  creationTimestamp: BigIntish | null | undefined,
  now: BigIntish
): TimeFrameOption[] {
  if (!isDefined(creationTimestamp)) {
    return timeFrameOptions
  }

  const vaultAgeSeconds = toBigInt(now) - toBigInt(creationTimestamp)
  const oneWeekSeconds = toBigInt(Time.s.fromPeriod('w'))

  // If vault is older than 1 week, all options are enabled
  if (vaultAgeSeconds >= oneWeekSeconds) {
    return timeFrameOptions
  }

  // If vault is younger than 1 week, only "1 week" is enabled
  return timeFrameOptions.map(option => {
    if (option.value === 'w') {
      return option
    }
    return { ...option, disabled: true }
  })
}
